import { Delete as DeleteIcon } from '@mui/icons-material';
import {
    Button,
    Divider,
    Grid,
    IconButton,
    List,
    ListItemButton,
    ListItemIcon,
    ListItemSecondaryAction,
    ListItemText,
    Typography,
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import {
    CollapsiblePaper,
    FileDropButton,
    HelperTooltip,
    PaperTitle,
    TitledBox,
} from 'cleveron-web-components';
import React from 'react';
import { Field } from 'react-final-form';
import { defineMessage, FormattedMessage } from 'react-intl';

import env from '../../env';
import { reactFinalFormHOC } from '../../form';
import { getIcon } from '../../utils/attachment';
import usePermissions from '../../utils/usePermissions';

const useStyles = makeStyles(() => ({
    listItemText: {
        overflowWrap: 'break-word',
    },
    uploadButton: {
        '& span': {
            fontSize: 14,
            textTransform: 'uppercase',
        },
    },
}));

const FileDrop = ({ id, input, className, handleSubmit, tooltipTitle }) => {
    return (
        <HelperTooltip title={tooltipTitle}>
            <FileDropButton
                Component={Button}
                componentProps={{
                    className,
                    size: 'small',
                    variant: 'text',
                }}
                id={id}
                name={input.name}
                onDrop={acceptedFiles => {
                    input.onChange(acceptedFiles[0]);
                    handleSubmit();
                }}
                textKey={
                    defineMessage({
                        defaultMessage: 'Upload',
                        id: 'attachments.buttons.upload',
                    }).id
                }
            />
        </HelperTooltip>
    );
};

export function Attachments({ attachments, disabled, handleDelete, id, getHref }) {
    const classes = useStyles();
    const { canDelete, canDownload } = usePermissions({
        canDelete: 'attachment_delete',
        canDownload: 'attachment_read',
    });

    return (
        <>
            {attachments?.length > 0 && (
                <List id={`${id}List`}>
                    {attachments.map((file, i) => (
                        <React.Fragment key={file.id}>
                            <ListItemButton
                                component="a"
                                disabled={!canDownload}
                                href={getHref && `${env.REACT_APP_SERVICE_API_URL}${getHref(file)}`}
                                id={`${id}${i}`}
                                target="_blank"
                            >
                                <ListItemIcon id={`${id}${i}Icon`}>
                                    {getIcon(file.mimeType ?? file.type)}
                                </ListItemIcon>
                                <ListItemText
                                    className={classes.listItemText}
                                    id={`${id}${i}Text`}
                                    primary={file.filename ?? file.name}
                                />
                                {handleDelete && canDelete && !disabled && (
                                    <ListItemSecondaryAction id={`${id}${i}DeleteIcon`}>
                                        <IconButton
                                            onClick={e => {
                                                e.stopPropagation();
                                                handleDelete(file);
                                            }}
                                            size="small"
                                        >
                                            <DeleteIcon />
                                        </IconButton>
                                    </ListItemSecondaryAction>
                                )}
                            </ListItemButton>
                            {i < attachments.length - 1 && (
                                <Divider component="li" id={`${id}${i}Divider`} />
                            )}
                        </React.Fragment>
                    ))}
                </List>
            )}
            {!attachments?.length && (
                <Typography color="textSecondary" variant="body2">
                    <FormattedMessage defaultMessage="No attachments" id="text.noAttachments" />
                </Typography>
            )}
        </>
    );
}

export function NcAttachmentsPaper({
    attachments,
    className,
    disabled,
    getHref,
    handleDelete,
    handleSubmit,
    id,
    tooltipTitle,
}) {
    const classes = useStyles();
    const { canUpload } = usePermissions({
        canUpload: 'attachment_create',
    });
    return (
        <CollapsiblePaper
            className={className}
            hasMoveDeleteButtons={false}
            headerComponent={
                <Grid alignItems="center" container item mt={0.5} xs={12}>
                    <Grid item xs>
                        <PaperTitle
                            id={`${id}Title`}
                            titleKey={
                                defineMessage({
                                    defaultMessage: 'Attachments',
                                    id: 'attachments.form.title',
                                }).id
                            }
                        />
                    </Grid>
                    <Grid item xs="auto">
                        {!disabled && canUpload && (
                            <Field
                                className={classes.uploadButton}
                                component={FileDrop}
                                handleSubmit={handleSubmit}
                                id={`${id}Attachment`}
                                name="attachment"
                                tooltipTitle={tooltipTitle}
                            />
                        )}
                    </Grid>
                </Grid>
            }
        >
            <Attachments
                attachments={attachments}
                disabled={disabled}
                getHref={getHref}
                handleDelete={handleDelete}
                id={id}
            />
        </CollapsiblePaper>
    );
}

export function NcAttachmentsBlock({
    attachments,
    bgcolor = 'background.default',
    disabled,
    getHref,
    handleDelete,
    handleSubmit,
    id,
    tooltipTitle,
    ...props
}) {
    const classes = useStyles();
    const { canUpload } = usePermissions({
        canUpload: 'attachment_create',
    });
    return (
        <TitledBox
            actions={
                !disabled &&
                canUpload && (
                    <Field
                        className={classes.uploadButton}
                        component={FileDrop}
                        handleSubmit={handleSubmit}
                        id={`${id}Attachment`}
                        name="attachment"
                        tooltipTitle={tooltipTitle}
                    />
                )
            }
            bgcolor={bgcolor}
            id={id}
            mt={2}
            {...props}
        >
            <Attachments
                attachments={attachments}
                disabled={disabled}
                getHref={getHref}
                handleDelete={handleDelete}
                id={id}
            />
        </TitledBox>
    );
}

export const AttachmentsPaper = reactFinalFormHOC()(NcAttachmentsPaper);
export const AttachmentsBlock = reactFinalFormHOC()(NcAttachmentsBlock);
